/**
 * Utilitário para Google Analytics
 * Fornece funções para inicializar e gerenciar o Google Analytics GA4
 * Implementação avançada com rastreamento detalhado para otimização
 */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

// Tipos para os parâmetros do GA4
interface GA4EventParams {
  [key: string]: any;
}

interface GA4UserProperties {
  [key: string]: {
    value: any;
  };
}

interface GA4ConfigParams {
  send_page_view?: boolean;
  page_title?: string;
  page_path?: string;
  page_location?: string;
  user_id?: string;
  debug_mode?: boolean;
  allow_google_signals?: boolean;
  allow_ad_personalization_signals?: boolean;
  cookie_prefix?: string;
  cookie_domain?: string;
  cookie_expires?: number;
}

interface GA4AdvancedParams extends GA4ConfigParams {
  groups?: string;
  traffic_type?: string;
  user_properties?: GA4UserProperties;
}

/**
 * Detecta informações importantes sobre o ambiente do usuário
 * @returns Objeto com dados de ambiente do usuário
 */
const detectEnvironmentInfo = (): Record<string, any> => {
  if (typeof window === 'undefined') return {};

  const userAgent = navigator.userAgent;
  const screenSize = {
    width: window.innerWidth,
    height: window.innerHeight
  };

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const browserName = (() => {
    if (userAgent.indexOf("Firefox") > -1) return "Firefox";
    else if (userAgent.indexOf("SamsungBrowser") > -1) return "Samsung Browser";
    else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) return "Opera";
    else if (userAgent.indexOf("Trident") > -1) return "Internet Explorer";
    else if (userAgent.indexOf("Edge") > -1) return "Edge";
    else if (userAgent.indexOf("Chrome") > -1) return "Chrome";
    else if (userAgent.indexOf("Safari") > -1) return "Safari";
    else return "Unknown";
  })();

  const deviceType = isMobile ? "Mobile" : "Desktop";
  const os = (() => {
    if (/Android/i.test(userAgent)) return "Android";
    if (/iPad|iPhone|iPod/i.test(userAgent)) return "iOS";
    if (/Win/i.test(userAgent)) return "Windows";
    if (/Mac/i.test(userAgent)) return "MacOS";
    if (/Linux/i.test(userAgent)) return "Linux";
    return "Unknown";
  })();

  const referrer = document.referrer || 'direct';
  const language = navigator.language || 'unknown';

  // Tentar detectar se o usuário está usando algum tipo de ad blocker
  const checkAdBlocker = () => {
    try {
      const testAd = document.createElement('div');
      testAd.innerHTML = '\u00A0';
      testAd.className = 'adsbox';
      // Pode ocorrer navegação/desmontagem rápida; proteger acessos ao body
      const body = document.body;
      if (!body) return false;
      body.appendChild(testAd);
      const adBlockActive = testAd.offsetHeight === 0;
      // Remoção defensiva
      if (testAd.parentNode) {
        testAd.parentNode.removeChild(testAd);
      } else if ((testAd as any).remove) {
        (testAd as any).remove();
      }
      return adBlockActive;
    } catch (e) {
      // Em caso de erro ao manipular DOM (ex.: navegação), assuma sem adblock
      return false;
    }
  };

  return {
    device_type: deviceType,
    browser: browserName,
    operating_system: os,
    screen_resolution: `${screenSize.width}x${screenSize.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language,
    referrer,
    has_ad_blocker: checkAdBlocker(),
    connection_type: (navigator as any).connection ? (navigator as any).connection.effectiveType : 'unknown',
    page_load_time: window.performance ? Math.round(performance.now()) : -1,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

/**
 * Inicializa o Google Analytics com configurações avançadas
 * @param measurementId - ID de medição do GA4 (formato G-XXXXXXXX)
 * @param config - Configurações avançadas adicionais
 */
export const initializeGoogleAnalytics = (measurementId: string = 'G-7XY72L8ZS7', config?: GA4AdvancedParams): void => {
  if (typeof window !== 'undefined') {
    // Verifica se já existe script do GA na página
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag"]`);

    if (!existingScript) {
      // Cria o script principal do gtag.js
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(gtagScript);

      // Inicializa o dataLayer
      window.dataLayer = window.dataLayer || [];

      // Define a função gtag
      window.gtag = function () {
        if (window.dataLayer) {
          window.dataLayer.push(arguments);
        }
      };

      // Configura os parâmetros iniciais
      if (window.gtag) {
        window.gtag('js', new Date());

        // Configurações avançadas do GA4
        const defaultConfig: GA4ConfigParams = {
          send_page_view: true,
          page_title: document.title,
          page_location: window.location.href,
          cookie_expires: 63072000, // 2 anos em segundos
          allow_google_signals: true,
          allow_ad_personalization_signals: true
        };

        // Mesclar configurações padrão com configurações personalizadas
        const finalConfig = { ...defaultConfig, ...config };
        window.gtag('config', measurementId, finalConfig);

        // Detecta e envia informações do ambiente do usuário
        const envInfo = detectEnvironmentInfo();

        // Envia propriedades do usuário diretamente aqui para evitar erros
        if (window.gtag) {
          window.gtag('set', 'user_properties', {
            device_type: { value: envInfo.device_type },
            browser: { value: envInfo.browser },
            operating_system: { value: envInfo.operating_system },
            screen_resolution: { value: envInfo.screen_resolution },
            language: { value: envInfo.language },
            connection_type: { value: envInfo.connection_type },
            timezone: { value: envInfo.time_zone },
            has_ad_blocker: { value: envInfo.has_ad_blocker ? 'yes' : 'no' }
          });
        }

        // Configurar evento personalizado para primeiro carregamento
        sendAnalyticsEvent('app_loaded', {
          load_time: envInfo.page_load_time,
          referrer: envInfo.referrer,
          viewport_size: envInfo.viewport_size
        });
      }
    } else {
      console.log('Google Analytics já está inicializado');
    }
  }
};

/**
 * Envia um evento para o Google Analytics
 * @param eventName - Nome do evento
 * @param eventParams - Parâmetros do evento
 */
export const sendAnalyticsEvent = (eventName: string, eventParams?: GA4EventParams): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Adiciona timestamp aos eventos
    const enrichedParams = {
      ...eventParams,
      event_time_ms: new Date().getTime(),
      session_id: getSessionId()
    };

    window.gtag('event', eventName, enrichedParams);
  }
};

/**
 * Define a página atual para o Google Analytics
 * @param path - Caminho da página (ex: /checkout)
 * @param title - Título da página
 */
export const setAnalyticsPage = (path: string, title?: string): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-7XY72L8ZS7', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.origin + path
    });

    // Rastreia a navegação de página como um evento também para mais dados
    sendAnalyticsEvent('page_view', {
      page_path: path,
      page_title: title || document.title,
      engagement_time_msec: 1,
      previous_path: sessionStorage.getItem('previous_path') || 'entry'
    });

    // Armazena o caminho atual como anterior para a próxima navegação
    sessionStorage.setItem('previous_path', path);

    console.log(`Página definida no GA: ${path}`);
  }
};

/**
 * Define o ID do usuário para o Google Analytics
 * @param userId - ID do usuário logado
 */
export const setAnalyticsUserId = (userId: string): void => {
  if (typeof window !== 'undefined' && window.gtag && userId) {
    window.gtag('config', 'G-7XY72L8ZS7', {
      user_id: userId
    });

    // Armazena localmente para uso em eventos futuros
    localStorage.setItem('analytics_user_id', userId);

    console.log(`ID do usuário definido no GA: ${userId}`);
  }
};

/**
 * Gera ou recupera um ID único de sessão
 * @returns ID da sessão atual
 */
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');

  if (!sessionId) {
    // Gera um ID de sessão único baseado em timestamp + número aleatório
    sessionId = `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }

  return sessionId;
};

/**
 * Rastreia interações do usuário com elementos importantes
 * Use esta função para rastrear interações com elementos específicos 
 */
export const trackElementInteraction = (element: string, action: string, metadata?: Record<string, any>): void => {
  sendAnalyticsEvent('element_interaction', {
    element_name: element,
    action_type: action,
    ...metadata
  });
};

/**
 * Rastreia o desempenho da página
 */
export const trackPagePerformance = (): void => {
  if (typeof window !== 'undefined' && window.performance) {
    // Espera até que a página esteja totalmente carregada
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        const dnsTime = perfData.domainLookupEnd - perfData.domainLookupStart;
        const tcpTime = perfData.connectEnd - perfData.connectStart;
        const serverResponseTime = perfData.responseEnd - perfData.requestStart;
        const domProcessingTime = perfData.domComplete - perfData.domLoading;

        sendAnalyticsEvent('page_performance', {
          page_load_time_ms: pageLoadTime,
          dns_time_ms: dnsTime,
          tcp_time_ms: tcpTime,
          server_response_time_ms: serverResponseTime,
          dom_processing_time_ms: domProcessingTime,
          url: window.location.pathname
        });
      }, 0);
    });
  }
};

/**
 * Rastreia erros da aplicação
 * @param errorMessage - Mensagem do erro
 * @param errorStack - Stack trace do erro
 * @param metadata - Metadados adicionais sobre o erro
 */
export const trackError = (errorMessage: string, errorStack?: string, metadata?: Record<string, any>): void => {
  sendAnalyticsEvent('app_error', {
    error_message: errorMessage,
    error_stack: errorStack?.substring(0, 500), // Limita o tamanho
    ...metadata
  });
};
